import { inject, injectable } from 'inversify'
import { nanoid } from 'nanoid/non-secure'
import { Group } from 'three'
import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { setup } from 'xstate'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { KeyEventParams, MouseEventParams } from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import { KeyCodes } from '@/shared/constants'
import type { AuxiliaryMouseHelper } from '../auxiliaryTool/auxiliaryMouseHelper'
import { type MouseTipHelper, MouseTipId } from '../auxiliaryTool/MouseTipHelper'
import type { CADController } from '../CADController'
import { DrawEntityCommand } from '../command/DrawEntityCommand'
import type { CADViewManager } from '../manager/CADViewManager'
import type { HistoryManager } from '../manager/HistoryManager'
import type { IPoint } from '../model/common/interface'
import {
  buildMLeaderLandingEnd,
  getDefaultMLeaderLandingDistance,
  getMLeaderTextGap,
  MLeaderEntity,
  type MLeaderLandingDirection,
} from '../model/entity'
import { createCadLine2Object } from '../object/material/CadLineFactory'
import { MaterialUtils } from '../object/material/MaterialUtils'
import type { EntitySelector } from '../selector/entitySelector'
import { formatMLeaderDefaultContents } from './formatMLeaderDefaultContents'

const mLeaderMachine = setup({
  actions: {
    showMouseTip: (..._args: any) => {},
    entryInput: () => {},
    entryFinished: () => {},
    entryCancelled: () => {},
  },
}).createMachine({
  id: 'drawMLeaderTool',
  initial: 'idle',
  states: {
    idle: {
      on: { NEXT: 'draw.point1' },
    },
    draw: {
      initial: 'point1',
      states: {
        point1: {
          entry: { type: 'showMouseTip', params: CadTipKey.draw.mLeader.leaderPoint },
          on: { NEXT: 'point2' },
        },
        point2: {
          entry: { type: 'showMouseTip', params: CadTipKey.draw.mLeader.textPoint },
          on: { NEXT: 'input' },
        },
        input: {
          entry: 'entryInput',
          on: {
            INPUT_SUBMIT: '#drawMLeaderTool.finished',
            INPUT_CANCEL: '#drawMLeaderTool.cancelled',
          },
        },
      },
    },
    finished: { entry: 'entryFinished', type: 'final' },
    cancelled: { entry: 'entryCancelled', type: 'final' },
  },
})

/**
 * 多重引线绘制工具，依次拾取引线起点与文字基线并输入文字。
 */
@injectable()
export class DrawMLeaderTool extends StateMachineTool<typeof mLeaderMachine> {
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.DrawMLeaderTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  @inject(TYPES.UnitPresentationService)
  private readonly unitPresentation!: UnitPresentationService

  protected machine = mLeaderMachine.provide({
    actions: {
      showMouseTip: (_context, tip) => {
        this.showMouseTip(tip)
      },
      entryInput: () => this.entryInput(),
      entryFinished: () => this.entryFinished(),
      entryCancelled: () => this.entryCancelled(),
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController

  private points: IPoint[] = []
  private tempPoint: IPoint | null = null
  private readonly group = new Group()
  private previewLineObject: Line2 | null = null
  private entity: MLeaderEntity | null = null
  private inputElement: HTMLTextAreaElement | null = null
  private cancelled = false

  protected onInitialize(entityData?: Partial<MLeaderEntity>): void {
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()
    this.inject(this.auxiliaryMouseHelper)

    this.entity = this.createEntity(entityData)
    this.historyManager.startCommand(
      new DrawEntityCommand(this.entity, this.viewManager, undefined, this.isCanCommit.bind(this))
    )
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.historyManager.commitCommand()
    this.previewLineObject && DrawUtils.disposeObject(this.previewLineObject)
    this.removeInputBox()
    this.group.parent?.remove(this.group)
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.eject(this.auxiliaryMouseHelper)

    this.points = []
    this.tempPoint = null
    this.previewLineObject = null
    this.entity = null
    this.cancelled = false
  }

  /**
   * 鼠标按下：左键依次记录引线端点，右键在输入阶段提交文字。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      if (this.checkState('draw.input')) {
        return
      }
      if (this.tempPoint && this.points.length < 2) {
        this.points.push({ x: this.tempPoint.x, y: this.tempPoint.y })
        this.tempPoint = null
        this.updateCanvas()
        this.send({ type: 'NEXT' })
      }
      return
    }

    if (params.event.button === 2 && this.checkState('draw.input')) {
      params.event.preventDefault()
      this.send({ type: 'INPUT_SUBMIT' })
    }
  }

  /**
   * 鼠标移动：更新引线末端预览点。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    if (this.checkState('draw.input')) {
      return
    }
    this.tempPoint = { x: params.worldPos.x, y: params.worldPos.y }
    this.mouseCapture(params)
    this.updateCanvas()
  }

  /**
   * 键盘按下：ESC 退出或取消输入；ENTER 在输入阶段提交文字。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      if (this.checkState('draw.input')) {
        params.event.preventDefault()
        this.send({ type: 'INPUT_CANCEL' })
        return
      }
      this.exit()
      return
    }

    if (params.event.keyCode === KeyCodes.ENTER && this.checkState('draw.input')) {
      params.event.preventDefault()
      this.send({ type: 'INPUT_SUBMIT' })
    }
  }

  /** 每帧刷新输入框位置 */
  onViewerUpdate() {
    this.updateInputBoxPosition()
  }

  private mouseCapture(params: MouseEventParams) {
    if (!this.tempPoint) {
      return
    }
    const startPoint = this.points.length > 0 ? this.points[this.points.length - 1] : undefined
    const finalPoint = this.auxiliaryMouseHelper.mouseCapture(params, { startPoint })
    this.tempPoint = {
      x: finalPoint.x,
      y: finalPoint.y,
    }
  }

  /************ 状态机状态处理 ************/
  private showMouseTip(tip: string) {
    this.mouseTipHelper.addTip(MouseTipId.Draw, tip)
  }

  private entryInput() {
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.tempPoint = null
    this.updateCanvas()
    const textPosition = this.getTextPosition()
    if (!textPosition) {
      return
    }
    this.inputElement = this.createInputBox(textPosition, this.getDefaultContents())
  }

  private entryFinished() {
    this.cancelled = false
    this.updateEntity()
    this.exit()
  }

  private entryCancelled() {
    this.cancelled = true
    this.exit()
  }

  private createInputBox(position: IPoint, defaultContents: string) {
    const screenPoint = this.controller.getScreenPosition(position)
    const inputElement = document.createElement('textarea')
    Object.assign(inputElement.style, {
      position: 'absolute',
      left: `${screenPoint.x}px`,
      top: `${screenPoint.y}px`,
      minWidth: '160px',
      minHeight: '48px',
      padding: '6px 8px',
      outline: 'none',
      resize: 'none',
      border: '1px solid #d0d0d0',
      backgroundColor: '#fff',
      color: '#000',
      transform: 'translate(0, -50%)',
    })
    inputElement.value = defaultContents
    this.controller.domElement?.parentElement?.appendChild(inputElement)

    inputElement.addEventListener('keydown', (event) => {
      event.stopPropagation()
      if (event.keyCode === KeyCodes.ENTER && !event.shiftKey) {
        event.preventDefault()
        this.send({ type: 'INPUT_SUBMIT' })
      } else if (event.keyCode === KeyCodes.ESC) {
        event.preventDefault()
        this.send({ type: 'INPUT_CANCEL' })
      }
    })
    inputElement.addEventListener('mousedown', (event) => {
      event.stopPropagation()
      if (event.button === 2) {
        event.preventDefault()
        this.send({ type: 'INPUT_SUBMIT' })
      }
    })
    inputElement.addEventListener('contextmenu', (event) => {
      event.preventDefault()
    })
    inputElement.focus()
    inputElement.select()
    return inputElement
  }

  private updateInputBoxPosition() {
    if (!this.inputElement) {
      return
    }
    const textPosition = this.getTextPosition()
    if (!textPosition) {
      return
    }
    const screenPoint = this.controller.getScreenPosition(textPosition)
    Object.assign(this.inputElement.style, {
      left: `${screenPoint.x}px`,
      top: `${screenPoint.y}px`,
    })
  }

  private removeInputBox() {
    if (!this.inputElement) {
      return
    }
    this.inputElement.remove()
    this.inputElement = null
  }

  private updateCanvas() {
    this.updatePreviewObjects()
    this.updateInputBoxPosition()
  }

  private updatePreviewObjects() {
    const points = [...this.points]
    if (this.tempPoint) {
      points.push(this.tempPoint)
    }
    if (points.length < 2 || !this.entity) {
      this.previewLineObject && (this.previewLineObject.visible = false)
      return
    }

    const { arrowPoint, elbowPoint, landingEnd } = this.getBranchGeometry(points[0], points[1])
    this.updatePreviewLine([arrowPoint, elbowPoint, landingEnd])
  }

  private updatePreviewLine(points: IPoint[]) {
    if (!this.previewLineObject && this.entity) {
      const geometry = DrawUtils.getLineGeometryByPoints(points)
      this.previewLineObject = createCadLine2Object(
        geometry,
        MaterialUtils.createMaterialByLineType(
          this.entity.lineType,
          MaterialUtils.getMaterialOptionByEntity(this.entity)
        )
      )
      this.previewLineObject.computeLineDistances()
      this.group.add(this.previewLineObject)
      return
    }
    if (!this.previewLineObject) {
      return
    }
    DrawUtils.updateLine2ByPoints(this.previewLineObject, points)
    this.previewLineObject.computeLineDistances()
    this.previewLineObject.visible = true
  }

  private createEntity(entityData?: Partial<MLeaderEntity>) {
    const entity = new MLeaderEntity({
      ...this.viewManager.getEntityDefaultData(entityData),
      id: nanoid(),
    })
    if (entityData?.textHeight) {
      entity.textHeight = entityData.textHeight
    }
    entity.landingGap = getDefaultMLeaderLandingDistance(
      entity.textHeight,
      entityData?.landingGap ?? entity.landingGap
    )
    entity.hasLanding = entityData?.hasLanding ?? true
    return entity
  }

  private updateEntity() {
    if (!this.entity || this.points.length < 2) {
      return
    }
    const { arrowPoint, elbowPoint, landingEnd, textPosition } = this.getBranchGeometry(
      this.points[0],
      this.points[1]
    )
    const contents = (this.inputElement?.value ?? this.getDefaultContents()).trim()

    Object.assign(this.entity, {
      contentType: 'mtext',
      branches: [
        {
          arrowPoint,
          vertices: [elbowPoint, landingEnd],
        },
      ],
      textPosition,
      contents,
      hasLanding: true,
      isTextPositionEdited: false,
    })
  }

  private isCanCommit() {
    if (this.cancelled || !this.entity) {
      return false
    }
    return this.entity.branches.length > 0 && !!this.entity.contents.trim()
  }

  private getTextPosition() {
    if (this.points.length < 2) {
      return null
    }
    return this.getBranchGeometry(this.points[0], this.points[1]).textPosition
  }

  private getDefaultContents() {
    if (this.points.length === 0) {
      return ''
    }
    return formatMLeaderDefaultContents(this.points[0], this.unitPresentation.preference)
  }

  private getBranchGeometry(startPoint: IPoint, elbowSourcePoint: IPoint) {
    const arrowPoint = { x: startPoint.x, y: startPoint.y }
    const elbowPoint = { x: elbowSourcePoint.x, y: elbowSourcePoint.y }
    const landingDirection: MLeaderLandingDirection = elbowPoint.x >= arrowPoint.x ? 1 : -1
    const landingEnd = buildMLeaderLandingEnd(elbowPoint, {
      direction: landingDirection,
      landingDistance: getDefaultMLeaderLandingDistance(
        this.entity?.textHeight,
        this.entity?.landingGap
      ),
    })
    const textGap = getMLeaderTextGap(this.entity?.textHeight, this.entity?.landingGap)
    const textPosition = {
      x: landingEnd.x + landingDirection * textGap,
      y: landingEnd.y,
    }
    return { arrowPoint, elbowPoint, landingEnd, textPosition }
  }
}
